import { Button, FlagKeyPill, TextField } from '@shared/ui';
import { useMemo, useState } from 'react';

import {
    deselectClient,
    filterClients,
    selectClient,
    selectClients,
    selectionChips,
    type ClientOption,
} from './client-selection';

/** How many matches are drawn at once; the selection itself has no cap. */
const VISIBLE_MATCHES = 50;

/**
 * Searchable client picker with the selection as removable chips (ACR-1596).
 * Controlled: the parent holds the ordered tenant ids.
 */
export function ClientSelector({
    clients,
    selected,
    onChange,
    disabled = false,
}: {
    clients: readonly ClientOption[];
    selected: readonly string[];
    onChange: (next: readonly string[]) => void;
    disabled?: boolean;
}) {
    const [search, setSearch] = useState('');
    const matches = useMemo(() => filterClients(clients, search), [clients, search]);
    const chosen = useMemo(() => new Set(selected), [selected]);
    const chips = useMemo(() => selectionChips(selected, clients), [selected, clients]);
    const unselected = matches.filter((client) => !chosen.has(client.tenantId));

    return (
        <div className="flex flex-col gap-3">
            <TextField
                label="Clients"
                value={search}
                onChange={setSearch}
                placeholder="Search by name, subdomain or id"
                disabled={disabled}
            />
            <ul aria-label="Matching clients" className="flex max-h-48 flex-col overflow-y-auto">
                {matches.slice(0, VISIBLE_MATCHES).map((client) => {
                    const isSelected = chosen.has(client.tenantId);
                    return (
                        <li key={client.tenantId}>
                            <button
                                type="button"
                                disabled={disabled}
                                aria-pressed={isSelected}
                                onClick={() => {
                                    onChange(
                                        isSelected
                                            ? deselectClient(selected, client.tenantId)
                                            : selectClient(selected, client.tenantId),
                                    );
                                }}
                                className="flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-[13px] hover:bg-surface-sunken"
                            >
                                <span>{client.displayName}</span>
                                <span className="text-xs text-content-muted">
                                    {isSelected
                                        ? 'Selected'
                                        : (client.subdomain ?? client.tenantId)}
                                </span>
                            </button>
                        </li>
                    );
                })}
                {matches.length === 0 && (
                    <li className="px-2 py-1.5 text-xs text-content-muted">No clients match.</li>
                )}
            </ul>
            {matches.length > VISIBLE_MATCHES && (
                <p className="text-xs text-content-muted">
                    Showing {String(VISIBLE_MATCHES)} of {String(matches.length)}. Refine the
                    search.
                </p>
            )}
            {unselected.length > 0 && (
                <div>
                    <Button
                        variant="ghost"
                        size="sm"
                        disabled={disabled}
                        onClick={() => {
                            onChange(
                                selectClients(
                                    selected,
                                    unselected.map((client) => client.tenantId),
                                ),
                            );
                        }}
                    >
                        Select all {String(unselected.length)} matching
                    </Button>
                </div>
            )}
            <div aria-label="Selected clients" className="flex flex-wrap gap-1.5">
                {chips.map((chip) => (
                    <span
                        key={chip.tenantId}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-accent-line bg-accent-surface px-2.5 py-1 text-[12.5px] font-medium text-accent-text"
                    >
                        <FlagKeyPill>{chip.label}</FlagKeyPill>
                        <button
                            type="button"
                            disabled={disabled}
                            aria-label={`Remove ${chip.label}`}
                            onClick={() => {
                                onChange(deselectClient(selected, chip.tenantId));
                            }}
                        >
                            ×
                        </button>
                    </span>
                ))}
            </div>
        </div>
    );
}
